<template>
  <div id="app">
    <NavBar ref="navbar" />
    <section id="site-content">
      <transition
        name="wipe"
        mode="out-in"
        @beforeLeave="beforeLeave"
        @afterEnter="afterEnter"
      >
        <router-view />
      </transition>
    </section>
    <Footer />
    <Wiper ref="wiper" />
  </div>
</template>

<script>
import NavBar from "@/components/Nav-Bar.vue";
import Wiper from "@/components/Wiper.vue";
import Footer from "@/components/Footer.vue";
import { TimelineLite, TweenLite, Power2 } from "gsap";
export default {
  components: {
    NavBar,
    Wiper,
    Footer
  },
  data() {
    return {
      animateEls: [],
      windowHeight: 0,
      isLoaded: false
    };
  },
  mounted() {
    this.initAnims();
    this.isLoaded = true;
    this.checkPageLink();
  },
  methods: {
    initAnims() {
      // init scroll trigger and check window size on resize
      this.scrollTrigger();
      this.windowHeight = window.innerHeight;
      window.addEventListener("resize", () => {
        this.windowHeight = window.innerHeight;
      });

      // collect a list of all the elements with the class of "animate" and push them into an array...need to do this way for ie so can use array methods slice, etc
      var animList = document.querySelectorAll(".animate");
      for (var i = 0; i < animList.length; i++) {
        this.animateEls.push(animList[i]);
      }

      // check if the top of the element is already in view or above the screen, if so call the animation and remove the element from the array
      this.animateEls.forEach((el, i) => {
        var elTop = el.getBoundingClientRect().top;
        if (elTop < this.windowHeight) {
          this.animateEls.splice(i, 1);
          this.animateElem(el);
        }
      });
    },
    scrollTrigger() {
      window.addEventListener("scroll", () => {
        // check each element in the array to see if it is in view, if it is call the animation function and remove from array
        this.animateEls.forEach((el, i) => {
          var elTop = el.getBoundingClientRect().top;
          if (elTop < this.windowHeight - 100) {
            this.animateEls.splice(i, 1);
            this.animateElem(el);
          }
        });
      });
    },
    animateElem(el) {
      // For sequence animation groups
      if (el.classList.contains("-sequence")) {
        // Create array of child nodes to be animated
        var animChilds = el.childNodes;
        var animEls = [];
        for (var i = 0; i < animChilds.length; i++) {
          if (animChilds[i].classList.contains("anim-el")) {
            animEls.push(animChilds[i]);
          }
        }
        // Sort AnimEls array by the data-anim order value
        animEls.sort(function(a, b) {
          return (
            a.getAttribute("data-anim-order") -
            b.getAttribute("data-anim-order")
          );
        });
        // Build timeline for animation based on the animation type of each item in animEls array
        var animTimeline = new TimelineLite();
        animEls.forEach(function(el) {
          var animationType = el.getAttribute("data-anim-type");
          if (animationType == "slide-right") {
            animTimeline.set(el, { transform: "translateX(-50px)" });
            animTimeline.add(
              TweenLite.to(el, 1, {
                opacity: 1,
                transform: "translateX(0)",
                ease: Power2.easeOut
              })
            );
          } else if (animationType == "slide-left") {
            animTimeline.set(el, { transform: "translateX(50px)" });
            animTimeline.add(
              TweenLite.to(el, 1, {
                opacity: 1,
                transform: "translateX(0)",
                ease: Power2.easeOut
              })
            );
          } else if (animationType == "slide-up") {
            animTimeline.set(el, { transform: "translateY(50px)" });
            animTimeline.add(
              TweenLite.to(el, 0.8, {
                opacity: 1,
                transform: "translateY(0)",
                ease: Power2.easeOut
              })
            );
          }
        });
        animTimeline.shiftChildren(-0.6, false, 1);
        animTimeline.play();
      } else {
        var timeline = new TimelineLite();
        timeline.fromTo(
          el,
          2,
          { opacity: 0, transform: "translateY(50px)" },
          { opacity: 1, transform: "translateY(0)", ease: Power2.easeOut }
        );
      }
    },
    beforeLeave() {
      this.$refs.wiper.wipeIn();
      document.getElementById("nav").classList.remove("nav-open");
    },
    afterEnter() {
      window.scrollTo(0, 0);
      this.$refs.wiper.wipeOut();
      this.initAnims();
      this.checkPageLink();
    },
    checkPageLink() {
      var hpLink = document.getElementById("hp-link");
      if (window.location.pathname == "/") {
        hpLink.addEventListener("click", function() {
          window.scrollTo(0, 0);
        });
      } else {
        hpLink.href = "/";
      }
    }
  }
};
</script>

<style lang="scss">
@import url("https://fonts.googleapis.com/css?family=Playfair+Display:400,400i");
@import url("https://use.typekit.net/pqx3toa.css");

// ========== BASE TEXT STYLES ===========
html,
body {
  font-family: europa, sans-serif;
  font-weight: 300;
  font-style: normal;
  font-size: 16px;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
h1,
h2,
h3,
h4,
h5,
h6 {
  font-family: "Playfair Display", serif;
  font-weight: 400;
  margin-bottom: 0;
  color:  #111d3c;
  line-height: 1.15em;
  &.subhead {
    font-style: italic;
  }
}

h1 {
  font-size: 3rem;
}
h2 {
  font-size: 2.5rem;
  &.subhead {
    font-size: 2rem;
  }
}
h3 {
  font-size: 2rem;
}
h4 {
  font-size: 1.5rem;
}
@media screen and (max-width: 768px) {
  h1 {
    font-size: 2rem;
  }
  h2 {
    font-size: 1.75rem;
    &.subhead {
      font-size: 1.5rem;
    }
  }
  h3 {
    font-size: 1.5rem;
  }
  h4 {
    font-size: 1.25rem;
  }
}

p {
  font-family: europa, sans-serif;
  font-weight: 400;
  font-style: normal;
  color:  #1a1818;
  line-height: 1.625rem;
  @media screen and (max-width: 768px) {
    line-height: 1.625rem;
  }
}
.white-text {
  color: #ffffff;
}
.gold-text {
  color: #dcb249;
}
.blue-text {
  color:  #111d3c;
}
.black-text {
  color:  #1a1818;
}
.gray-text {
  color:  #efefef;
}

hr {
  border-top: none;
  border-bottom: 2px solid #dcb249;
}

// ========== BUTTON STYLES ===========
a {
  font-family: europa, sans-serif;
  font-weight: 400;
  font-style: normal;
}
a.btn {
  font-family: europa, sans-serif;
  font-style: normal;
  text-transform: uppercase;
  letter-spacing: 0.2rem;
  font-size: 0.875rem;
  font-weight: 300;
  color: #ffffff;
  text-decoration: none;
  padding: 8px 28px;
  border-width: 1px;
  border-style: solid;
  border-color:  #111d3c;
  background-color:  #111d3c;
  transition: all 0.3s ease;
  &:hover {
    color: #dcb249;
    text-decoration: none;
  }
  &.-hollow_white {
    color: #ffffff;
    border-color: #ffffff;
    background-color: transparent;
    &:hover {
      background-color: #ffffff;
      color:  #111d3c;
      text-decoration: none;
    }
  }
}

#app {
  display: block;
  width: 100%;
}
#site-content {
  margin-top: 100px;
  @media screen and (max-width: 960px) {
    margin-top: 60px;
  }
}

img {
  max-width: 100%;
}

.hero-section {
  height: 70vh;
  min-height: 700px;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  @media screen and (max-width: 350px) {
    min-height: 550px;
  }
}

// ========== WIPER TRANSITION STYLES ===========
.wipe-enter-active,
.wipe-leave-active {
  transition-duration: 0.4s;
}

// ========== BACKGROUND TYPES ==================

.image-bg {
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  &.-marble_bg {
    background-image: url(./assets/marble-bg.jpg);
  }
  &.-transaction_bg {
    background-color:  #111d3c;
    background-image: url(./assets/hp-transaction-bg.jpg);
  }
}

.pattern-bg {
  background-repeat: repeat;
  &.-woven_dark {
    background:  #111d3c;
  }
}

.image-container {
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  &.-square_image {
    height: 600px;
    @media screen and (max-width: 768px) {
      height: 300px;
    }
  }
  &.-rect_image {
    height: 400px;
    @media screen and (max-width: 768px) {
      height: 200px;
    }
  }
}

.anim-el {
  opacity: 0;
}
.load-anim {
  opacity: 0;
  transform: translateY(50px);
}
</style>
